import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import { useEffect } from 'react'
import { ChevronRight } from 'lucide-react'
import { TabBar } from './TabBar'

/** Listas e grades aproveitam a tela; leitura e formulario ficam numa coluna. */
const ROTAS_LARGAS = ['/categorias', '/empresas', '/buscar', '/excursoes', '/videos']

function larguraDoConteudo(pathname: string): string {
  if (pathname === '/') return '' // a home controla a propria largura (hero de ponta a ponta)
  if (pathname === '/noticias' || ROTAS_LARGAS.some((r) => pathname.startsWith(r))) return 'max-w-6xl'
  return 'max-w-2xl'
}

/**
 * Ocupa a tela inteira em qualquer aparelho: celular, tablet (inclusive em
 * paisagem), WebView e navegador de computador. Cabecalho e barra inferior
 * vao de ponta a ponta; o conteudo cresce ate um limite de leitura.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return (
    <div className="flex min-h-dvh w-full flex-col bg-surface">
      <main
        className="w-full flex-1"
        style={{
          paddingTop: 'calc(var(--altura-header) + var(--safe-top))',
          // Folga extra no fim: o ultimo item nunca fica colado na barra.
          paddingBottom: 'calc(var(--altura-tabbar) + var(--safe-bottom) + 1rem)',
          paddingLeft: 'var(--safe-left)',
          paddingRight: 'var(--safe-right)',
        }}
      >
        <div className={`mx-auto w-full ${larguraDoConteudo(pathname)}`}>{children}</div>
      </main>
      <TabBar />
    </div>
  )
}

/**
 * Cabecalho de secao com marcador verde. O app antigo usava um h3 sublinhado
 * em navy; o marcador cumpre o mesmo papel de ancorar a secao sem cortar a
 * largura toda da tela.
 */
export function Secao({
  titulo,
  descricao,
  verTudo,
  children,
  className,
}: {
  titulo: string
  descricao?: string
  verTudo?: { para: string; rotulo?: string }
  children: ReactNode
  className?: string
}) {
  return (
    <section className={className ?? 'mt-8'}>
      <div className="mb-3.5 flex items-start justify-between gap-3 px-5">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-label text-navy-600">
            <span className="h-3.5 w-1 rounded-full bg-verde-500" aria-hidden />
            {titulo}
          </h2>
          {descricao && (
            <p className="mt-1.5 pl-3 text-[13px] leading-snug text-ink-mute">{descricao}</p>
          )}
        </div>

        {verTudo && (
          <Link
            to={verTudo.para}
            className="mt-0.5 flex shrink-0 items-center gap-0.5 text-xs font-semibold text-verde-600"
          >
            {verTudo.rotulo ?? 'Ver todas'}
            <ChevronRight size={14} aria-hidden />
          </Link>
        )}
      </div>
      {children}
    </section>
  )
}
